/**
 * Z-Index System
 *
 * Centralized z-index values for shared UI (toolbars, feedback, overlays) and
 * sitecontrol-web–specific layers (charts, navigation).
 *
 * Usage:
 * - Use static values for fixed z-index needs
 * - Prefer named tokens over magic numbers
 *
 * Layer Structure:
 * - Base (0-9): Charts, backgrounds, base content
 * - Content (10-99): Interactive elements, toolbars, widgets
 * - Overlay (10-999): Loading overlays, modals, feedback panels
 * - Navigation: Derived from overlay.panel for menus and chrome
 */

const OVERLAY_PANEL = 100;

export const Z_INDEX = {
  // Base Layer (0-9): Charts, backgrounds, base content
  base: {
    /** Ground level base elements (outlines, backgrounds) */
    ground: 1,
    /** Elevated elements above ground (menus, toolbars, battery head, etc.) */
    elevated: 5,
    /** Chart base layer (lowest chart z-index) */
    chart: 0,
    /** Chart data series layer */
    chartSeries: 1,
  },

  // Content Layer (10-99): Interactive elements, toolbars, widgets
  content: {
    /** Floating/fixed interactive controls (toolbars, FAB buttons, action buttons, grid controls) */
    floating: 30,
    /** Tooltips for floating controls (above floating) */
    floatingTooltip: 31,
    /** Sticky content that appears above floating controls */
    sticky: 50,
  },

  // Overlay Layer (10-999): Loading overlays, modals, feedback panels
  overlay: {
    /** Widget-level loading overlay - overlays content within widgets/cards */
    loading: 10,
    /** Error state overlay (loading + 10) */
    error: 20,
    /** Panel overlay (feedback, modals, navigation menus) */
    panel: OVERLAY_PANEL,
  },

  // Navigation Layer: Navigation, menus, top-level UI (calculated from overlay.panel)
  navigation: {
    /** Navigation menu, tooltips in nav, header, landing page menus */
    menu: OVERLAY_PANEL,
    /** Sidebar chrome (above the panel/menu layer) */
    sidebar: OVERLAY_PANEL + 1,
  },
} as const;
